import { useEffect, useRef } from 'react';

/**
 * A ref that is true while the component is mounted.
 *
 * Hooks that fetch write their result back with setState, and a response can arrive after the
 * component has gone — the user navigated away, or a test finished. React 18 removed the
 * "state update on an unmounted component" warning, so that happens silently in the browser;
 * under the test runner the late callback runs against a torn-down environment and surfaces as
 * an unhandled "window is not defined", which fails CI with every test passing.
 *
 * Guard each setState that follows an await:
 *
 *   const isMounted = useIsMounted();
 *   const res = await fetchWithAuth(url);
 *   if (!isMounted.current) return;
 *   setData(await res.json());
 *
 * The ref is set back to true on mount rather than only cleared on unmount, because React 18's
 * StrictMode mounts, unmounts and remounts a component in development — leaving it false after
 * that first teardown would silently suppress every later update and render an empty page.
 */
export default function useIsMounted() {
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return mountedRef;
}
